'use client';

import { Check, Mic, SlidersHorizontal, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Badge } from '@/app/components/ui/heroui-badge';
import { cn } from '@/app/utils';
import type { StudioStep } from '../_hooks';

const STEPS = [
  { key: 'avatar', icon: User, badge: '/images/offline-digital-human/step-badge-1.webp' },
  { key: 'speech', icon: Mic, badge: '/images/offline-digital-human/step-badge-2.webp' },
  { key: 'settings', icon: SlidersHorizontal, badge: '/images/offline-digital-human/step-badge-3.webp' },
] as const;

export function StudioSteps({
  step,
  ready,
  onChange,
  overlapBanner = false,
}: {
  step: StudioStep;
  ready: [boolean, boolean];
  onChange: (step: StudioStep) => void;
  /** 压在上方横幅底部：导航高 72px，向上重叠一半 */
  overlapBanner?: boolean;
}) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  return (
    <nav
      aria-label={t('stepProgress', { current: step + 1 })}
      className={cn(
        'relative z-10 flex justify-center',
        overlapBanner ? '-mt-[36px] sm:-mt-[26px] sm:px-3' : 'mt-6 sm:mt-4',
      )}
    >
      <div className="flex h-[72px] w-fit max-w-full gap-1 rounded-full border border-[#2C2F38] bg-[#15171C]/40 p-1.5 shadow-[0_10px_28px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:h-[52px] sm:w-full sm:p-1">
        {STEPS.map(({ key, icon: Icon, badge }, index) => {
          const selected = step === index;
          const completed = index < 2 && ready[index];
          const disabled = index === 1 ? !ready[0] : index === 2 ? !ready.every(Boolean) : false;
          return (
            <button
              key={key}
              type="button"
              aria-current={selected ? 'step' : undefined}
              disabled={disabled}
              onClick={() => onChange(index as StudioStep)}
              className={cn(
                'flex min-h-11 w-[280px] items-center gap-3 rounded-full px-2.5 text-left font-normal transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2 motion-reduce:transition-none sm:min-h-0 sm:w-auto sm:min-w-0 sm:flex-1 sm:justify-center sm:gap-1 sm:px-1',
                'disabled:cursor-not-allowed',
                selected ? 'bg-white/[0.04] text-white backdrop-blur-md' : 'text-white enabled:hover:bg-system-white08',
              )}
            >
              {/* 按 heroui-badge 示例：彩色渐变圆作底，图标叠在上面；已完成的步骤右上角挂绿色对勾。还没到的步骤只降饱和度，文字保持白色 */}
              <Badge.Anchor>
                <span
                  className={cn(
                    'relative flex size-10 items-center justify-center overflow-hidden rounded-3xl text-white transition-[filter] sm:size-7',
                    // 还没走到的步骤（排在当前步骤之后）降饱和度，与能否点击无关
                    index > step && 'saturate-[0.15]',
                    // 未选中的步骤图标底座整体 24% 透明度
                    !selected && 'opacity-[0.24]',
                  )}
                  style={{ backgroundImage: `url(${badge})`, backgroundSize: 'cover' }}
                >
                  <Icon className="size-4 shrink-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" aria-hidden="true" />
                </span>
                {completed && !selected ? (
                  <Badge color="success" size="sm" className="border-[#15171C]">
                    <Check className="size-2.5" aria-hidden="true" />
                  </Badge>
                ) : null}
              </Badge.Anchor>
              <span className="min-w-0 sm:hidden">
                <span className="truncate text-sm leading-5">
                  <span className="mr-1.5 tabular-nums opacity-60">{index + 1}</span>
                  {t(`steps.${key}.title`)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
